<template>
  <div class="dc" :class="{ 'dc--visible': mounted }">
    <!-- ━━━ Stage 1: Success moment ━━━ -->
    <div class="dc-success" :class="{ show: stage >= 1 }">
      <div class="dc-check-ring">
        <svg class="dc-check-svg" viewBox="0 0 52 52">
          <circle class="dc-check-circle" cx="26" cy="26" r="24" />
          <path class="dc-check-path" d="M15 26l7 7 15-15" />
        </svg>
      </div>
    </div>

    <!-- ━━━ Stage 2: Hero ━━━ -->
    <div class="dc-hero" :class="{ show: stage >= 2 }">
      <h1 class="dc-h1">Ready to run this on <em>your</em> store?</h1>
      <p class="dc-lead">
        Everything you just saw works out of the box on Shopify. Install the
        app, connect your catalog, and the AI starts reaching your customers —
        no credit card, no setup call.
      </p>
    </div>

    <!-- ━━━ Stage 3: What they saw ━━━ -->
    <div class="dc-recap" :class="{ show: stage >= 3 }">
      <div
        class="dc-recap-item"
        v-for="(item, i) in recapItems"
        :key="i"
        :style="{ transitionDelay: `${i * 0.06}s` }"
      >
        <span class="dc-recap-check">✓</span>
        <span>{{ item }}</span>
      </div>
    </div>

    <!-- ━━━ Stage 4: Stats ━━━ -->
    <div class="dc-numbers" :class="{ show: stage >= 4 }">
      <div class="dc-num" v-for="(s, i) in stats" :key="i">
        <div class="dc-num-value">
          {{ counting ? animatedValues[i] : s.end
          }}<span class="dc-num-unit">{{ s.suffix }}</span>
        </div>
        <div class="dc-num-label">{{ s.label }}</div>
      </div>
    </div>

    <!-- ━━━ Stage 5: CTA ━━━ -->
    <div class="dc-cta" :class="{ show: stage >= 5 }">
      <button class="dc-install" @click="$emit('install')">
        Install on Shopify — Free
        <span class="dc-arrow">→</span>
      </button>
      <div class="dc-meta">No credit card · 2 min setup · Cancel anytime</div>
    </div>

    <!-- ━━━ Bottom ━━━ -->
    <div class="dc-bottom" :class="{ show: stage >= 5 }">
      <button class="dc-link" @click="$emit('explore')">Keep exploring</button>
      <button class="dc-link" @click="$emit('replay')">Replay tour</button>
    </div>
  </div>
</template>

<script setup lang="ts">
/* eslint-disable no-undef */
import { ref, onMounted, onUnmounted } from 'vue';

defineEmits<{
  install: [];
  explore: [];
  replay: [];
}>();

const recapItems = [
  'Live dashboard with customer insights',
  'Top customers ranked by value',
  'AI-written emails & SMS for each customer',
  'Smart scheduling & timing',
  'Behavior-triggered targeting',
  'Editable templates you control'
];

const stats = [
  { end: 32, suffix: '%', prefix: '', label: 'Avg. revenue lift' },
  { end: 4.2, suffix: 'x', prefix: '', label: 'More repeat buyers' },
  { end: 68, suffix: '%', prefix: '', label: 'Cart recovery' },
  { end: 2, suffix: ' min', prefix: '', label: 'Setup time' }
];

const mounted = ref(false);
const stage = ref(0);
const counting = ref(false);
const animatedValues = ref(stats.map(() => 0));

let timers: ReturnType<typeof setTimeout>[] = [];

function animateCounters() {
  counting.value = true;
  const dur = 1000;
  const steps = 36;
  const iv = dur / steps;
  let step = 0;

  const t = setInterval(() => {
    step++;
    const eased = 1 - Math.pow(1 - step / steps, 3);
    animatedValues.value = stats.map((s) => {
      const v = s.end * eased;
      return s.suffix === 'x' || s.suffix === 'min'
        ? Math.round(v * 10) / 10
        : Math.round(v);
    });
    if (step >= steps) {
      clearInterval(t);
      animatedValues.value = stats.map((s) => s.end);
      counting.value = false;
    }
  }, iv);
}

onMounted(() => {
  mounted.value = true;
  [80, 400, 850, 1350, 1900].forEach((ms, i) => {
    const t = setTimeout(() => {
      stage.value = i + 1;
      if (i === 3) animateCounters();
    }, ms);
    timers.push(t);
  });
});

onUnmounted(() => timers.forEach(clearTimeout));
</script>

<style scoped>
/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
   ROOT
   ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */
.dc {
  position: relative;
  width: 100%;
  border-radius: 24px;
  padding: 40px 36px 28px;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  overflow: hidden;
  opacity: 0;
  transform: translateY(8px);
  transition: opacity 0.45s ease, transform 0.45s ease;
}

.dc--visible {
  opacity: 1;
  transform: translateY(0);
}

/* ━━━ Shared stage transition ━━━ */
.dc-success,
.dc-hero,
.dc-recap,
.dc-numbers,
.dc-cta,
.dc-bottom {
  opacity: 0;
  transform: translateY(12px);
  transition: opacity 0.5s ease, transform 0.5s ease;
}

.dc-success.show,
.dc-hero.show,
.dc-recap.show,
.dc-numbers.show,
.dc-cta.show,
.dc-bottom.show {
  opacity: 1;
  transform: translateY(0);
}

/* ━━━ Stage 1: Check ━━━ */
.dc-success {
  text-align: center;
  margin-bottom: 16px;
}

.dc-check-ring {
  display: inline-block;
  width: 56px;
  height: 56px;
}

.dc-check-svg {
  width: 56px;
  height: 56px;
}

.dc-check-circle {
  fill: none;
  stroke: #d1fae5;
  stroke-width: 2;
  stroke-dasharray: 151;
  stroke-dashoffset: 151;
}

.dc-success.show .dc-check-circle {
  animation: dc-circle 0.55s 0.15s ease forwards;
}

.dc-check-path {
  fill: none;
  stroke: #10b981;
  stroke-width: 3;
  stroke-linecap: round;
  stroke-linejoin: round;
  stroke-dasharray: 40;
  stroke-dashoffset: 40;
}

.dc-success.show .dc-check-path {
  animation: dc-tick 0.35s 0.6s ease forwards;
}

@keyframes dc-circle {
  to {
    stroke-dashoffset: 0;
  }
}

@keyframes dc-tick {
  to {
    stroke-dashoffset: 0;
  }
}

/* ━━━ Stage 2: Hero ━━━ */
.dc-hero {
  text-align: center;
  margin-bottom: 24px;
}

.dc-h1 {
  margin: 0;
  font-size: clamp(26px, 3.4vw, 40px);
  line-height: 1.15;
  font-weight: 800;
  letter-spacing: -0.025em;
  color: #111827;
}

.dc-h1 em {
  font-style: normal;
  color: #6366f1;
}

.dc-lead {
  margin: 12px auto 0;
  max-width: 52ch;
  font-size: 15px;
  line-height: 1.6;
  color: #6b7280;
  font-weight: 500;
}

/* ━━━ Stage 3: Recap ━━━ */
.dc-recap {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 6px;
  margin-bottom: 24px;
  padding: 0 8px;
}

.dc-recap-item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 8px;
  background: #f9fafb;
  border: 1px solid #f3f4f6;
  font-size: 13px;
  font-weight: 600;
  color: #374151;
  opacity: 0;
  transform: translateY(6px);
  transition: opacity 0.3s ease, transform 0.3s ease;
}

.dc-recap.show .dc-recap-item {
  opacity: 1;
  transform: translateY(0);
}

.dc-recap-check {
  color: #10b981;
  font-weight: 800;
  font-size: 13px;
}

/* ━━━ Stage 4: Numbers ━━━ */
.dc-numbers {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0;
  margin-bottom: 24px;
  border: 1px solid #f0f1f3;
  border-radius: 14px;
  overflow: hidden;
}

.dc-num {
  text-align: center;
  padding: 18px 8px;
  background: #fafbfc;
}

.dc-num + .dc-num {
  border-left: 1px solid #f0f1f3;
}

.dc-num-value {
  font-size: clamp(26px, 3vw, 38px);
  font-weight: 800;
  line-height: 1;
  color: #111827;
  letter-spacing: -0.02em;
  font-variant-numeric: tabular-nums;
}

.dc-num-unit {
  font-size: 0.5em;
  font-weight: 700;
  color: #9ca3af;
  margin-left: 1px;
}

.dc-num-label {
  margin-top: 6px;
  font-size: 12px;
  font-weight: 600;
  color: #9ca3af;
}

/* ━━━ Stage 5: CTA ━━━ */
.dc-cta {
  text-align: center;
  margin-bottom: 20px;
  padding: 0 8px;
}

.dc-install {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: min(480px, 100%);
  border: none;
  border-radius: 12px;
  padding: 16px 24px;
  font-size: clamp(16px, 1.8vw, 19px);
  font-weight: 800;
  color: white;
  background: #16a34a;
  cursor: pointer;
  transition: background 0.15s ease, transform 0.15s ease, box-shadow 0.15s ease;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.dc-install:hover {
  background: #15803d;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(22, 163, 74, 0.25);
}

.dc-install:active {
  transform: translateY(0);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.dc-arrow {
  font-size: 20px;
  transition: transform 0.15s ease;
}

.dc-install:hover .dc-arrow {
  transform: translateX(2px);
}

.dc-meta {
  margin-top: 10px;
  font-size: 12px;
  font-weight: 600;
  color: #9ca3af;
}

/* ━━━ Bottom ━━━ */
.dc-bottom {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
}

.dc-link {
  background: none;
  border: none;
  color: #9ca3af;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  padding: 4px 0;
  transition: color 0.15s ease;
}

.dc-link:hover {
  color: #6b7280;
}

/* ━━━ Responsive ━━━ */
@media (max-width: 640px) {
  .dc {
    padding: 28px 20px 24px;
  }

  .dc-numbers {
    grid-template-columns: repeat(2, 1fr);
  }

  .dc-num:nth-child(3),
  .dc-num:nth-child(4) {
    border-top: 1px solid #f0f1f3;
  }

  .dc-num:nth-child(2) + .dc-num {
    border-left: 0;
  }

  .dc-recap-item {
    font-size: 12px;
    padding: 5px 10px;
  }
}
</style>
